import { useState } from 'react'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { useToast } from '../../../components/ui/ToastProvider'
import { useCartCount } from '../../../lib/cart/cart-count-context'
import { addCatalogResultToCart } from '../catalog-add-to-cart'
import { leadTimeLabel, resultDisplayName, sourceBadgeLabel } from '../format'
import type { CatalogResultCard } from '../types'
import { CatalogResultThumb } from './CatalogResultThumb'

export interface RecentlyViewedQuickViewModalProps {
  /** `null` renders nothing (closed) — the caller owns which curated card,
   * if any, is open (`RecentlyViewed.tsx`'s `openCard` state), mirroring
   * `ConfiguratorDrawer`'s `open`/`card` split. */
  readonly card: CatalogResultCard | null
  readonly onClose: () => void
}

/**
 * The "Recently viewed" row's click-through destination (ADR-0128 Decision
 * 1/5, `Fictiv Platform.dc.html` ~L4574-4588) — there is no route/PDP for a
 * finalized catalog SKU (ADR-0128 C3), so this quick-view modal IS the
 * destination, exactly as the design draws it: category caption, image,
 * name, "part number · brand", a Lead time/Price two-column row, and a
 * full-width "Add to cart · <price>" button.
 *
 * Reuses the shared `Modal` shell (focus trap/Escape/backdrop/restore come
 * for free, same as `ConfiguratorDrawer`) and the existing
 * `addCatalogResultToCart` cart-write path — no second `POST /cart/lines`
 * call site. `category` rides `headerContent` (the modal's uppercase
 * caption) rather than the default `<h2>`; `title` still carries the
 * card's name as the dialog's accessible name (`Modal`'s own contract for
 * `headerContent` callers).
 */
export function RecentlyViewedQuickViewModal({ card, onClose }: RecentlyViewedQuickViewModalProps) {
  const { showToast } = useToast()
  const { setLineCount } = useCartCount()
  const [adding, setAdding] = useState(false)

  if (!card) return null

  const { sourceable, availability, category } = card
  const name = resultDisplayName(sourceable)
  const brand = sourceBadgeLabel(sourceable.provenance)
  const priceLabel = sourceable.indicativePrice
    ? `${sourceable.indicativePrice.currency} ${sourceable.indicativePrice.amount}`
    : 'Quote required'

  async function handleAddToCart() {
    setAdding(true)
    try {
      const result = await addCatalogResultToCart(sourceable)
      if (result.lineCount === null) return // 401 — handled globally.
      setLineCount(result.lineCount)
      showToast(`Added ${name} to cart`)
      onClose()
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not add that item.', 'error')
    } finally {
      setAdding(false)
    }
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={name}
      maxWidth={420}
      headerContent={
        category ? <span className="catalog-quick-view-category">{category}</span> : undefined
      }
    >
      <div className="catalog-quick-view-body">
        <CatalogResultThumb
          imageRef={sourceable.imageRef}
          name={name}
          className="catalog-quick-view-image"
        />
        <p className="catalog-quick-view-name">{name}</p>
        {(sourceable.catalogSku || brand) && (
          <p className="catalog-quick-view-subline">
            {[sourceable.catalogSku, brand].filter(Boolean).join(' · ')}
          </p>
        )}
        <div className="catalog-quick-view-facts">
          <div>
            <p className="catalog-quick-view-fact-label">Lead time</p>
            <p className="catalog-quick-view-fact-value">{leadTimeLabel(availability)}</p>
          </div>
          <div className="catalog-quick-view-fact-price">
            <p className="catalog-quick-view-fact-label">Price</p>
            <p className="catalog-quick-view-fact-value">{priceLabel}</p>
          </div>
        </div>
        <Button
          type="button"
          variant="primary"
          size="lg"
          className="catalog-quick-view-add"
          loading={adding}
          onClick={() => void handleAddToCart()}
        >
          Add to cart · {priceLabel}
        </Button>
      </div>
    </Modal>
  )
}
